import { useEffect, useState } from 'react';

import {
  AuthenticatedConnectUser,
  SDK_EVENT,
  paragon,
} from '@useparagon/connect';

import accountsAPI from '@/api/accounts';
import { PARAGON_PROJECT_ID } from '@/config/env';

import useAuth from './useAuth';

async function getParagonUserToken(orgID: number): Promise<string> {
  const response = await accountsAPI.getIntegrationToken(orgID);
  return response.data.token;
}

export default function useParagonAuth(): {
  user?: AuthenticatedConnectUser;
  error?: Error;
} {
  const { orgID } = useAuth();

  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<AuthenticatedConnectUser | undefined>();
  const [error, setError] = useState<Error | undefined>();

  useEffect(() => {
    if (orgID) {
      getParagonUserToken(orgID).then(setToken).catch(setError);
    }
  }, [orgID]);

  // Listen for account state changes
  useEffect(() => {
    const listener = () => {
      if (paragon) {
        const authedUser = paragon.getUser();
        if (authedUser.authenticated) {
          setUser({ ...authedUser });
        }
      }
    };

    paragon.subscribe(SDK_EVENT.ON_INTEGRATION_INSTALL, listener);
    paragon.subscribe(SDK_EVENT.ON_INTEGRATION_UNINSTALL, listener);

    return () => {
      paragon.unsubscribe(SDK_EVENT.ON_INTEGRATION_INSTALL, listener);
      paragon.unsubscribe(SDK_EVENT.ON_INTEGRATION_UNINSTALL, listener);
    };
  }, [paragon]);

  useEffect(() => {
    if (paragon && token && !error) {
      paragon
        .authenticate(PARAGON_PROJECT_ID, token)
        .then(() => {
          const authedUser = paragon.getUser();
          if (authedUser.authenticated) {
            setUser(authedUser);
          }
        })
        .catch(setError);
    }
  }, [paragon, token, error]);

  return { user, error };
}
